import React, { useEffect, useRef, useState } from 'react';
import { RING } from '../../wall/wallTokens';
import { T, RADIUS, BODY_FONT } from '../messagesModel';
import { prefersReducedMotion } from '../../transitions/portalMotion';
import { barText, labelFor, PLAN, EASE } from './planModel';

/**
 * The detection surface (board 1c): a 48px ring-bordered pill above the message bar —
 * `PLAN DETECTED` (or `PLAN FOUND`), the title · time, REVIEW, ×. Hidden while the viewer types.
 * Plays the arrival once per suggestion: the scan line sweeps down the thread, the pill rises
 * as a dot and stretches, two ripples, the label types on with a blinking caret, the ring
 * slides three times, then the title and the buttons slide in. Reduced motion shows the end.
 *
 * The keyframes also carry prPlanMark for the highlights, so one <style> serves the thread.
 */
export const PLAN_KEYFRAMES = `
@keyframes prPlanSweep{0%{top:0;opacity:0}12%{opacity:1}88%{opacity:1}100%{top:calc(100% - 8px);opacity:0}}
@keyframes prPlanPillIn{0%{opacity:0;transform:translateY(28px);clip-path:inset(0 calc(50% - 22px) round 999px)}35%{opacity:1;transform:translateY(0);clip-path:inset(0 calc(50% - 22px) round 999px)}100%{opacity:1;transform:translateY(0);clip-path:inset(0 0 round 999px)}}
@keyframes prPlanRipple{0%{transform:translate(-50%,-50%) scale(1);opacity:.9}100%{transform:translate(-50%,-50%) scale(3.2);opacity:0}}
@keyframes prPlanRingSlide{0%{background-position:0 0,0 0}100%{background-position:0 0,200% 0}}
@keyframes prPlanType{0%{clip-path:inset(0 100% 0 0)}100%{clip-path:inset(0 0 0 0)}}
@keyframes prPlanCaret{0%,49%{opacity:1}50%,100%{opacity:0}}
@keyframes prPlanRise{0%{opacity:0;transform:translateX(-8px)}100%{opacity:1;transform:translateX(0)}}
@keyframes prPlanMark{0%{background-size:0% 100%;text-decoration-color:transparent}100%{background-size:100% 100%;text-decoration-color:${PLAN.pink}}}
@keyframes prPlanFade{0%{opacity:0}100%{opacity:1}}
@keyframes prPlanSheetUp{0%{transform:translateY(100%)}100%{transform:translateY(0)}}
@keyframes prPlanSheetDown{0%{transform:translateY(0)}100%{transform:translateY(100%)}}
@keyframes prPlanModalIn{0%{opacity:0;transform:translateY(14px) scale(.97)}100%{opacity:1;transform:translateY(0) scale(1)}}
@keyframes prPlanModalOut{0%{opacity:1;transform:scale(1)}100%{opacity:0;transform:translateY(10px) scale(.97)}}
@keyframes prPlanPop{0%{transform:scale(.4);opacity:0}100%{transform:scale(1);opacity:1}}
@keyframes prPlanPing{0%{transform:scale(1);opacity:.8}100%{transform:scale(1.9);opacity:0}}
@keyframes prPlanPillGrow{0%{clip-path:inset(0 calc(100% - 52px) 0 0 round 999px)}100%{clip-path:inset(0 0 0 0 round 999px)}}
@keyframes prPlanBurst{0%{transform:scale(1)}100%{transform:scale(46)}}
@keyframes prPlanSpin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){[data-plan-anim]{animation:none!important;clip-path:none!important;opacity:1!important;transform:none!important}}
`;

const X = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
);

/** The scan line: absolutely positioned over the thread's scroller (the parent must be `position: relative`). */
export function PlanSweep({ play }) {
  if (!play) return null;
  return (
    <div data-plan-anim data-plan-sweep aria-hidden style={{ position: 'absolute', left: 0, right: 0, height: 2, zIndex: 20, pointerEvents: 'none', background: RING, opacity: 0, animation: 'prPlanSweep 720ms cubic-bezier(.4,0,.2,1) both' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 2, height: 56, background: 'linear-gradient(to top, rgba(253,25,159,.10), rgba(253,25,159,0))' }} />
    </div>
  );
}

export default function PlanBar({ draft, hidden = false, onReview, onDismiss, animateKey = null }) {
  const reduced = prefersReducedMotion();
  // The arrival plays once per suggestion (by id + the run that produced it), not on every render.
  const playedFor = useRef(null);
  const [play, setPlay] = useState(false);
  const key = animateKey || draft?.id;
  useEffect(() => {
    if (!draft || !key) return;
    if (playedFor.current === key) return;
    playedFor.current = key;
    setPlay(!reduced);
  }, [draft, key, reduced]);
  if (!draft || hidden) return null;

  const anim = (name) => (play ? name : 'none');
  const label = labelFor(draft);
  const steps = label.length;

  return (
    <div data-plan-bar style={{ position: 'relative', padding: '0 20px 8px', flexShrink: 0 }}>
      {/* Two ripples from where the dot lands. */}
      <span data-plan-anim aria-hidden style={{ position: 'absolute', left: '50%', top: 24, width: 44, height: 44, borderRadius: '50%', border: `2px solid ${PLAN.cyan}`, boxSizing: 'border-box', pointerEvents: 'none', opacity: 0, animation: anim('prPlanRipple 900ms cubic-bezier(0,0,.2,1) 640ms both') }} />
      <span data-plan-anim aria-hidden style={{ position: 'absolute', left: '50%', top: 24, width: 44, height: 44, borderRadius: '50%', border: `2px solid ${PLAN.pink}`, boxSizing: 'border-box', pointerEvents: 'none', opacity: 0, animation: anim('prPlanRipple 900ms cubic-bezier(0,0,.2,1) 820ms both') }} />
      <div
        data-plan-anim
        className="font-mono"
        style={{
          position: 'relative', height: 48, borderRadius: RADIUS.pill, border: '2px solid transparent',
          background: `linear-gradient(${T.white},${T.white}) padding-box, ${RING} border-box`, backgroundSize: '100% 100%, 200% 100%',
          display: 'flex', alignItems: 'center', gap: 10, padding: '0 6px 0 16px', boxSizing: 'border-box',
          animation: play ? `prPlanPillIn 760ms ${EASE} 520ms both, prPlanRingSlide 2.4s linear 1.2s 3` : 'none',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
          <span data-plan-anim data-plan-label style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.12em', whiteSpace: 'nowrap', animation: anim(`prPlanType 480ms steps(${steps},end) 1000ms both`) }}>{label}</span>
          {play && <span data-plan-anim aria-hidden style={{ width: 6, height: 11, background: PLAN.magenta, animation: 'prPlanCaret 380ms steps(1,end) 1000ms 4 both', opacity: 0 }} />}
        </span>
        <span data-plan-anim data-plan-bar-text style={{ flex: 1, minWidth: 0, fontFamily: BODY_FONT, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: T.grey600, animation: anim(`prPlanRise 380ms ${EASE} 1380ms both`) }}>{barText(draft)}</span>
        <button
          type="button"
          data-plan-anim
          data-plan-review
          onClick={onReview}
          className="hover:bg-white hover:text-black"
          style={{ animation: anim(`prPlanRise 320ms ${EASE} 1520ms both`), height: 34, padding: '0 14px', borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: T.ink, color: T.white, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', flexShrink: 0, cursor: 'pointer', transition: 'background-color 200ms, color 200ms' }}
        >
          REVIEW
        </button>
        <button type="button" data-plan-anim data-plan-dismiss onClick={onDismiss} aria-label="Not a plan" title="Not a plan" style={{ animation: anim(`prPlanRise 320ms ${EASE} 1520ms both`), width: 30, height: 30, border: 0, background: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.grey500, flexShrink: 0, cursor: 'pointer' }}>
          {X}
        </button>
      </div>
    </div>
  );
}

/** `NO PLAN SUGGESTIONS IN THIS CHAT FOR 24 HOURS · UNDO` — in the thread, where the bar was. */
export function PlanDismissedLine({ onUndo }) {
  return (
    <div data-plan-dismissed className="font-mono" style={{ flexShrink: 0, alignSelf: 'center', margin: '12px 0', display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', fontSize: 10, letterSpacing: '.1em', color: T.grey500 }}>
      <span>NO PLAN SUGGESTIONS IN THIS CHAT FOR 24 HOURS</span>
      <button type="button" onClick={onUndo} style={{ background: 'none', border: 0, padding: 0, fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textDecoration: 'underline', color: T.ink, cursor: 'pointer', fontFamily: 'inherit' }}>UNDO</button>
    </div>
  );
}
